// biome-ignore-all lint/suspicious/noUnnecessaryConditions: React refs change in effect cleanup and event handlers outside this static control flow.
import {
  type LockerverseAuction,
  type LockerverseAuctionItem,
  type LockerverseAuctionPurchaseResult,
  LockerverseSdkError,
} from "@lockerverse/sdk/auctions";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import type { StripeAddressElementChangeEvent } from "@stripe/stripe-js";
import { type FormEvent, useEffect, useId, useRef, useState } from "react";
import { AuctionCheckoutView } from "./auction-checkout-view.tsx";
import type {
  LockerverseAuctionItemProps,
  LockerverseAuctionPurchaseRecovery,
} from "./auction-item-types.ts";
import {
  type AuctionSelection,
  auctionExpectedPricing,
  completeAuctionAction,
  createAuctionConfirmationToken,
  getAuctionSelectionError,
  getAuctionSelectionPricing,
  hasAuctionCheckoutChanged,
} from "./auction-payment.ts";
import { notifyHostCallback } from "./host-callback.ts";
import { type ContactErrors, validateContact } from "./payment-validation.ts";
import { getInternationalPhone, resolvePhoneCountry } from "./phone.ts";

type AuctionCheckoutProps = LockerverseAuctionItemProps & {
  item: LockerverseAuctionItem;
  selection: AuctionSelection;
  onReview: (auction: LockerverseAuction, message: string) => void;
  onBack: () => void;
  contact?: { email: string; phone: string };
  onContactChange?: (contact: { email: string; phone: string }) => void;
  onIncludeServiceFeeChange?: (value: boolean) => void;
  onInteractionLockChange?: (locked: boolean) => void;
};

type CheckoutState =
  | "ready"
  | "busy"
  | "pending"
  | "retry"
  | "complete"
  | "uncertain";

function getSubmitLabel(
  state: CheckoutState,
  hasRecovery: boolean,
  isPurchase: boolean
) {
  if (state === "busy") {
    return isPurchase ? "Processing..." : "Authorizing...";
  }
  if (state === "retry") {
    return "Retry original purchase";
  }
  if (hasRecovery) {
    return "Check purchase status";
  }
  return isPurchase ? "Buy Now" : "Authorize Bid";
}

export function AuctionCheckout(props: AuctionCheckoutProps) {
  const { auction, item, selection, client, onReview, onBack } = props;
  const stripe = useStripe();
  const elements = useElements();
  const id = useId();
  const [email, setEmail] = useState(props.contact?.email ?? "");
  const [phone, setPhone] = useState(props.contact?.phone ?? "");
  const [phoneCountry, setPhoneCountry] = useState<string>(() =>
    resolvePhoneCountry(props.contact?.phone ?? "")
  );
  const [contactErrors, setContactErrors] = useState<ContactErrors>({});
  const [sameAsBilling, setSameAsBilling] = useState(true);
  const [billing, setBilling] =
    useState<StripeAddressElementChangeEvent | null>(null);
  const [shipping, setShipping] =
    useState<StripeAddressElementChangeEvent | null>(null);
  const [recovery, setRecovery] = useState(props.resumePurchase ?? null);
  const [state, setState] = useState<CheckoutState>(
    props.resumePurchase ? "pending" : "ready"
  );
  const [message, setMessage] = useState<string | null>(null);
  const purchaseRequestId = useRef<string | null>(
    props.resumePurchase?.submission.clientRequestId ?? null
  );
  const isTerminal = state === "complete" || state === "uncertain";
  const busy = useRef<boolean>(false);
  const mounted = useRef<boolean>(true);
  const callbacks = useRef(props);
  callbacks.current = props;
  const outcomeReported = useRef(false);

  function transition(next: CheckoutState) {
    if (mounted.current) {
      setState(next);
    }
    notifyHostCallback(
      callbacks.current.onInteractionLockChange,
      next !== "ready" && next !== "complete"
    );
  }
  function showMessage(value: string | null) {
    if (mounted.current) {
      setMessage(value);
    }
  }
  function reportUncertain(kind: "bid" | "purchase") {
    transition(kind === "bid" ? "uncertain" : "pending");
    showMessage(
      kind === "bid"
        ? "We could not confirm your bid. Do not submit another bid until you check your email or contact the auction organizer."
        : "We could not confirm the purchase result. Check its status using the same purchase request."
    );
    if (!outcomeReported.current) {
      outcomeReported.current = true;
      notifyHostCallback(callbacks.current.onUncertain, kind);
    }
  }
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const isPurchase =
    Boolean(recovery ?? props.resumePurchase) || item.saleMode === "buy_now";
  const pricing =
    recovery?.pricing ??
    props.resumePurchase?.pricing ??
    getAuctionSelectionPricing(auction, item, selection);

  const payment =
    recovery?.payment ?? props.resumePurchase?.payment ?? auction.payment;

  function rememberPurchase(value: LockerverseAuctionPurchaseRecovery | null) {
    if (mounted.current) {
      setRecovery(value);
    }
    notifyHostCallback(callbacks.current.onPurchaseRecoveryChange, value);
  }

  function finishPurchase(result: LockerverseAuctionPurchaseResult) {
    if (result.status === "paid") {
      purchaseRequestId.current = null;
      rememberPurchase(null);
      transition("complete");
      showMessage(`Purchase complete. Receipt: ${result.purchaseId}`);
      if (!outcomeReported.current) {
        outcomeReported.current = true;
        notifyHostCallback(callbacks.current.onPurchaseComplete, result);
      }
    } else if (result.status === "pending_payment") {
      transition("pending");
      showMessage(
        "Your purchase is pending. Check its status before making another payment."
      );
    } else {
      purchaseRequestId.current = null;
      rememberPurchase(null);
      transition("ready");
      showMessage(
        `This purchase is ${result.status.replaceAll("_", " ")}. You can start a new purchase.`
      );
    }
  }

  function readContact() {
    const phoneNumber = getInternationalPhone(phone, phoneCountry);
    const contact = validateContact({ email, phone: phoneNumber }, true);
    setContactErrors(contact.errors);
    if (!contact.value) {
      document
        .getElementById(`${id}-${contact.errors.email ? "email" : "phone"}`)
        ?.focus();
      return null;
    }
    return { email: contact.value.email, phone: phoneNumber };
  }

  async function prepareSubmission(
    contact: { email: string; phone: string } | null
  ) {
    if (!contact) {
      throw new Error("Check the contact fields below.");
    }
    if (!(stripe && elements && auction.payment)) {
      throw new Error("Payment details are still loading.");
    }
    const current = await client.load(auction.slug);
    if (!mounted.current) {
      return null;
    }
    const currentItem = current.items.find(
      (candidate) => candidate.id === item.id
    );
    if (!currentItem) {
      onReview(current, "This item is no longer available.");
      return null;
    }
    const selectionError = getAuctionSelectionError(
      current,
      currentItem,
      selection
    );
    if (
      selectionError ||
      hasAuctionCheckoutChanged(auction, current, item, currentItem, selection)
    ) {
      onReview(
        current,
        selectionError ??
          "The checkout details changed. Review them before continuing."
      );
      return null;
    }
    const confirmationToken = await createAuctionConfirmationToken({
      billing,
      collectShippingAddress: item.collectShippingAddress,
      elements,
      email: contact.email,
      phone: contact.phone,
      shipping: sameAsBilling ? billing : shipping,
      stripe,
    });
    if (!mounted.current) {
      return null;
    }
    return {
      confirmationToken,
      email: contact.email,
      includeServiceFee: selection.includeServiceFee,
      phone: contact.phone,
      ...auctionExpectedPricing(pricing),
    };
  }

  async function sendBid(
    submission: NonNullable<Awaited<ReturnType<typeof prepareSubmission>>>
  ) {
    const result = await client.createBid(auction.slug, item.id, {
      ...submission,
      amount: selection.amount,
    });
    if (result.requiresAction) {
      if (!(mounted.current && stripe)) {
        reportUncertain("bid");
        return;
      }
      await completeAuctionAction(stripe, result);
    }
    transition("complete");
    showMessage("Bid submitted. Watch your email for bid and auction updates.");
    if (!outcomeReported.current) {
      outcomeReported.current = true;
      notifyHostCallback(callbacks.current.onBidSubmitted, result);
    }
  }

  async function resolvePurchase(
    result: LockerverseAuctionPurchaseResult,
    active: LockerverseAuctionPurchaseRecovery
  ) {
    if (result.requiresAction) {
      if (!(mounted.current && stripe)) {
        reportUncertain("purchase");
        return;
      }
      await completeAuctionAction(stripe, result);
      const updated = await client.getPurchaseStatus(
        auction.slug,
        item.id,
        active.submission.clientRequestId
      );
      if (updated) {
        finishPurchase(updated);
      } else {
        transition("pending");
        showMessage(
          "Payment status is not available yet. Check again shortly."
        );
      }
      return;
    }
    finishPurchase(result);
    if (!mounted.current && result.status === "pending_payment") {
      reportUncertain("purchase");
    }
  }

  async function checkPurchase(active: LockerverseAuctionPurchaseRecovery) {
    const result = await client.getPurchaseStatus(
      auction.slug,
      item.id,
      active.submission.clientRequestId
    );
    if (result) {
      await resolvePurchase(result, active);
    } else {
      if (!mounted.current) {
        reportUncertain("purchase");
        return;
      }
      transition("retry");
      showMessage(
        "No purchase is recorded yet. You can retry the original purchase with the same request ID."
      );
    }
  }

  async function clearRejectedPurchase(
    error: LockerverseSdkError,
    active: LockerverseAuctionPurchaseRecovery
  ) {
    const result = await client.getPurchaseStatus(
      auction.slug,
      item.id,
      active.submission.clientRequestId
    );
    if (result) {
      await resolvePurchase(result, active);
      return;
    }
    rememberPurchase(null);
    transition("ready");
    showMessage(
      `The purchase was rejected. Review the item and payment details before trying again. (${error.status ?? "validation"})`
    );
  }

  async function handleFailure<Failure>(
    error: Failure,
    requestSent: boolean,
    active: LockerverseAuctionPurchaseRecovery | null,
    wasRecovery: boolean
  ) {
    const uncertain =
      requestSent &&
      (!(error instanceof LockerverseSdkError) || error.recoveryRecommended);
    showMessage(
      error instanceof Error
        ? error.message
        : "Unable to complete this request."
    );
    if (isPurchase && active) {
      if (
        !wasRecovery &&
        error instanceof LockerverseSdkError &&
        !error.recoveryRecommended
      ) {
        try {
          await clearRejectedPurchase(error, active);
          return;
        } catch {
          /* Keep the original request when status cannot be established. */
        }
      }
      reportUncertain("purchase");
    } else if (uncertain) {
      reportUncertain("bid");
    } else {
      transition("ready");
    }
  }

  function getPurchaseRequestId() {
    purchaseRequestId.current ??= globalThis.crypto.randomUUID();
    return purchaseRequestId.current;
  }

  function canSendPurchase() {
    if (mounted.current) {
      return true;
    }
    if (!recovery) {
      rememberPurchase(null);
      transition("ready");
    }
    return false;
  }

  async function sendOrCheckPurchase(
    active: LockerverseAuctionPurchaseRecovery,
    statusOnly: boolean
  ) {
    if (statusOnly) {
      await checkPurchase(active);
      return;
    }
    if (!canSendPurchase()) {
      return;
    }
    const result = await client.createPurchase(
      auction.slug,
      item.id,
      active.submission
    );
    await resolvePurchase(result, active);
  }

  const checkoutPayment = stripe && elements && payment;

  function startSubmission() {
    if (busy.current || isTerminal) {
      return null;
    }
    if (!checkoutPayment) {
      showMessage("Payment details are still loading.");
      return null;
    }
    const contact = recovery ? null : readContact();
    if (!(recovery || contact)) {
      return null;
    }
    return { checkoutPayment, contact, retryOriginal: state === "retry" };
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const start = startSubmission();
    if (!start) {
      return;
    }
    const { contact, checkoutPayment: readyPayment, retryOriginal } = start;
    busy.current = true;
    outcomeReported.current = false;
    transition("busy");
    showMessage(null);
    let requestSent = false;
    let activeRecovery = recovery;
    try {
      if (!activeRecovery) {
        const submission = await prepareSubmission(contact);
        if (!(submission && mounted.current)) {
          transition("ready");
          return;
        }
        if (!isPurchase) {
          requestSent = true;
          await sendBid(submission);
          return;
        }
        activeRecovery = Object.freeze({
          auctionId: auction.id,
          item,
          itemId: item.id,
          payment: Object.freeze({ ...readyPayment }),
          pricing,
          submission: Object.freeze({
            ...submission,
            clientRequestId: getPurchaseRequestId(),
            paymentMethodTypes: Object.freeze(["card"] as const),
            quantity: selection.quantity,
          }),
        });
        rememberPurchase(activeRecovery);
      }
      requestSent = true;
      await sendOrCheckPurchase(
        activeRecovery,
        Boolean(recovery) && !retryOriginal
      );
    } catch (error) {
      await handleFailure(
        error,
        requestSent,
        activeRecovery,
        recovery !== null && !retryOriginal
      );
    } finally {
      busy.current = false;
    }
  }

  const buttonText = getSubmitLabel(state, recovery !== null, isPurchase);

  const locked = state !== "ready";
  return (
    <AuctionCheckoutView
      buttonText={buttonText}
      communityName={props.communityName ?? auction.name}
      contactErrors={contactErrors}
      email={email}
      id={id}
      includeServiceFee={selection.includeServiceFee}
      isPurchase={isPurchase}
      isTerminal={isTerminal}
      item={item}
      locked={locked}
      message={message}
      onBack={state === "ready" ? onBack : undefined}
      onBillingChange={setBilling}
      onEmailChange={(value) => {
        setEmail(value);
        props.onContactChange?.({
          email: value,
          phone: getInternationalPhone(phone, phoneCountry),
        });
      }}
      onIncludeServiceFeeChange={props.onIncludeServiceFeeChange}
      onPaymentLoadError={() => showMessage("Unable to load payment form.")}
      onPhoneChange={({ value, country }) => {
        setPhone(value);
        setPhoneCountry(country);
        props.onContactChange?.({
          email,
          phone: getInternationalPhone(value, country),
        });
      }}
      onSameAsBillingChange={setSameAsBilling}
      onShippingChange={setShipping}
      onSubmit={submit}
      phone={phone}
      phoneCountry={phoneCountry}
      pricing={pricing}
      quantity={selection.quantity}
      sameAsBilling={sameAsBilling}
      serviceFeeCents={
        recovery
          ? pricing.serviceFeeCents
          : getAuctionSelectionPricing(auction, item, {
              ...selection,
              includeServiceFee: auction.serviceFeeEnabled,
            }).serviceFeeCents
      }
      serviceFeeEnabled={auction.serviceFeeEnabled}
      showFields={!(recovery || isTerminal)}
      submitDisabled={state === "busy" || !stripe || !elements}
    />
  );
}
